import { ControlIcons } from 'owa-control-icons';
import * as Constants from './constants';
import { getIconClassName } from '@uifabric/styling/lib/index';
import { isBrowserEdge } from 'owa-user-agent/lib/userAgent';
import strings from 'owa-strings/owa-mail-strings';

const CONTENT_EDITABLE_ATTRIBUTE_NAME = 'contenteditable';

let closeButtonStyles = require('../styles/closeButtonStyle.scss');
let classNames = require('classnames/bind');

export enum LinkPreviewType {
    WebpagePreview,
    VideoPreview,
}

export interface BaseLinkPreviewObject {
    type: LinkPreviewType;

    title: string;
    thumbnailImageUrl: string;
    description: string;
    url: string;

    anchor?: HTMLAnchorElement;
    border?: HTMLDivElement;
    closeButtonContainer?: HTMLDivElement;
}

export function createLinkPreviewBorder(previewType: string, lp: BaseLinkPreviewObject) {
    // Encode the url and put it in the border id. This is used by the jsmvvm implementation of
    // link previews when rehydrating previews in the reading pane
    let encodedUrl = window.btoa(lp.url);
    encodedUrl = encodedUrl.replace(new RegExp('\\+', 'g'), '-');
    encodedUrl = encodedUrl.replace(new RegExp('/', 'g'), ':');
    encodedUrl = encodedUrl.replace(new RegExp('=', 'g'), '.');

    lp.border = document.createElement('div');
    lp.border.id = `${Constants.LINK_PREVIEW_BORDER_ID_BASE}_${previewType}${encodedUrl}`;
    // Since the link preview border id is already in use for when a link preview is sent from
    // jsmvvm but opened in React, use the class instead to store an id shared across
    // all elements within the link preview border. This will be used for link preview removal
    lp.border.className =
        Constants.LINK_PREVIEW_BORDER_ID_BASE + Constants.createLinkPreviewIdNumber();
    lp.border.setAttribute(CONTENT_EDITABLE_ATTRIBUTE_NAME, 'false');
    setStyleForBorder(lp.border);
}

export function addCloseButton(
    lp: BaseLinkPreviewObject,
    onPreviewRemoved: (linkPreview: BaseLinkPreviewObject) => void
) {
    lp.closeButtonContainer = document.createElement('div');
    lp.closeButtonContainer.id = Constants.createLinkPreviewId(
        lp.border.className,
        Constants.LINK_PREVIEW_CLOSE_BUTTON_CONTAINER_ID
    );
    lp.closeButtonContainer.className = closeButtonStyles.closeButtonContainer;
    lp.closeButtonContainer.tabIndex = 0;
    lp.closeButtonContainer.title = strings.removeLinkPreview;
    lp.closeButtonContainer.setAttribute('role', 'button');

    let closeButtonIcon = document.createElement('span');
    closeButtonIcon.id = Constants.createLinkPreviewId(
        lp.border.className,
        Constants.LINK_PREVIEW_CLOSE_BUTTON_ID
    );
    closeButtonIcon.className = classNames(
        closeButtonStyles.icon,
        getIconClassName(ControlIcons.Cancel)
    );
    lp.closeButtonContainer.appendChild(closeButtonIcon);

    lp.border.appendChild(document.createComment(Constants.COMPOSE_ONLY_CONTENT_START));
    lp.border.appendChild(lp.closeButtonContainer);
    lp.border.appendChild(document.createComment(Constants.COMPOSE_ONLY_CONTENT_END));

    const onCloseLinkPreview = (evt: Event) => {
        if (evt instanceof KeyboardEvent && evt.keyCode != Constants.ENTER_CHARCODE) {
            return;
        }

        evt.stopPropagation();
        if (lp.anchor) {
            lp.anchor.id =
                Constants.NO_LINK_PREVIEW_ANCHOR_ID + Constants.createLinkPreviewIdNumber();
        }
        onPreviewRemoved(lp);
    };

    lp.closeButtonContainer.addEventListener('click', onCloseLinkPreview);
    lp.closeButtonContainer.addEventListener(
        isBrowserEdge() ? 'keydown' : 'keyup',
        onCloseLinkPreview
    );
}

function setStyleForBorder(element: HTMLElement) {
    element.style.width = '100%';
    element.style.marginTop = '16px';
    element.style.marginBottom = '16px';
    element.style.position = 'relative';
    element.style.maxWidth = '800px';
    element.style.minWidth = '424px';
}
